import { connect } from 'react-redux';
import compose from 'recompose/compose';
import {
  AirLeg,
  AirSegmentDetails,
  OptionId,
} from '../../../../../../../models/itinerary';
import { AppState } from '../../../../../../../store/model';
import { getAircraftCodeshareDetails } from '../../../../../../../store/reducers/segment-details/reducer';
import {
  getProviderNames,
  getSegmentOption,
} from '../../../../../../../store/reducers/segment-options/selectors';
import { formatFlightNumber } from '../../../../../../../utils/format';
import { commaSeparated, unique } from '../../../../../../../utils/helpers';
import { AirProviderDetails as _AirProviderDetails } from '../../../../../../lab/ProviderDetails/AirProviderDetails';

interface OwnProps {
  optionId: OptionId;
  segmentId: number;
}

interface StateProps {
  codeshare?: string;

  /**
   * @example
   * "Air New Zealand"
   */
  providerName: string;

  /**
   * @example
   * "NZ123, QA845*"
   */
  flightNumbers: string;
}

interface AirProps extends StateProps {}

/**
 * Format a list of flight numbers from AirLeg
 *
 * If only one leg, will be formatted like "NZ729 Boeing 737-800".
 *
 * If more than one leg, will be formatted like "VA813*, NZ729" - the asterisk is for operated airlines
 */
const getFlightNumbers = (legs: AirLeg[]) => {
  if (legs.length === 1) {
    const { flightNumber, aircraft } = legs[0];

    return [`${formatFlightNumber(flightNumber)} ${aircraft}`];
  }

  /**
   * Format flight number with asterisk if the flight is operated by another airline
   *
   * @example
   * formatFlight("NZ123", "NZ")
   * // returns: "NZ123*"
   *
   * @example
   * formatFlight("NZ123", undefined)
   * // returns: "NZ123"
   */
  const formatFlight = ({ flightNumber, operatedByAirlineCode }: AirLeg) => {
    return `${formatFlightNumber(flightNumber)}${
      !!operatedByAirlineCode ? '*' : ''
    }`;
  };

  /**
   * Format each flight, and remove duplicate flights so that it's not rendered more than once
   */
  return unique(legs).map(formatFlight);
};

const mapStateToProps = (
  state: AppState,
  { optionId, segmentId }: OwnProps,
) => {
  const { legs } = getSegmentOption(segmentId, optionId)(
    state,
  ) as AirSegmentDetails;

  return {
    providerName: commaSeparated(getProviderNames(segmentId, optionId)(state)),
    flightNumbers: commaSeparated(getFlightNumbers(legs)),
    codeshare: getAircraftCodeshareDetails(legs),
  };
};

const enhance = compose<AirProps, OwnProps>(connect(mapStateToProps));

/**
 * Air provider details (Container)
 *
 * @see AirProviderDetails
 *
 * @example
 * "Virgin Airways"
 * "VA815*, NZ875"
 * "*NZ1850 operated by Qantas Airways"
 */
export const AirProviderDetailsContainer = enhance(_AirProviderDetails);

AirProviderDetailsContainer.displayName = 'AirProviderDetails';
